import React, {useEffect, useState} from "react";
import {Button, Card, CardBody, CardHeader, CardTitle} from "reactstrap";
import {IExoTemplate} from "../../../../store/exo/types";
import TelegramEditor from "../TelegramEditor/TelegramEditor";
import FeatherIcon from "feather-icons-react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import usePermissions from "../../../../hooks/usePermissions";
import {ExoService} from "../../../../services/exo.service";

interface CardExoTemplateProps {
    template: IExoTemplate
    onEdit?: (id: number) => void;
}

const CardExoTemplate: React.FC<CardExoTemplateProps> = ({
                                                             template,
                                                             onEdit,
                                                         }) => {
    const queryClient = useQueryClient();
    const {hasPermission} = usePermissions();

    const [removeApprove, setRemoveApprove] = useState<boolean>(false);
    const [sureToRemove, setSureToRemove] = useState<boolean>(false);

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyExoTemplates"]});
    }

    const {mutate: removeTemplate, isPending: isPendingRemove} = useMutation({
        mutationFn: (templateId: number) => ExoService.removeTemplate(templateId),
        onSuccess: onSuccess,
        onError: () => setRemoveApprove(false),
        mutationKey: ["keyRemoveExoTemplate"],
    });

    const handleEditClick = () => {
        if (onEdit) onEdit(template.id)
    }

    const handleRemoveClick = () => {
        if (!sureToRemove) {
            setSureToRemove(true)
            return;
        }

        setSureToRemove(false)
        setRemoveApprove(true)
        removeTemplate(template.id)
    }

    useEffect(() => {
        if (sureToRemove) {
            const timer = setTimeout(() => setSureToRemove(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [sureToRemove]);

    const isPendingRemoveFull = (isPendingRemove || !!queryClient.isFetching({queryKey: ["keyExoTemplates"]})) && removeApprove;

    return (
        <Card className={isPendingRemoveFull ? "opacity-50 pe-none" : undefined}>
            <CardHeader className="p-3">
                <div className="d-flex align-items-center justify-content-between">
                    <CardTitle tag="h5" className="fs-5 m-0">
                        {template.name}
                    </CardTitle>

                    <div className="d-flex align-items-center gap-1">
                        {hasPermission('edit-echo-templates') &&
                            <Button className="d-flex p-1" color="success" onClick={handleEditClick}
                                    disabled={!hasPermission('edit-echo-templates')}>
                                <FeatherIcon icon="edit-2" size={16}/>
                            </Button>}

                        {hasPermission('delete-echo-templates') &&
                            <Button className="d-flex p-1" color="danger" onClick={handleRemoveClick}
                                    disabled={!hasPermission('delete-echo-templates')}>
                                {sureToRemove ?
                                    <FeatherIcon icon="check" size={16}/> :
                                    isPendingRemoveFull ?
                                        <i className="mdi mdi-loading mdi-spin font-size-16 lh-1"></i> :
                                        <FeatherIcon icon="trash-2" size={16}/>}
                            </Button>}
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-3">
                <TelegramEditor value={template.body_msg} readOnly/>
            </CardBody>
        </Card>
    )
};

export default CardExoTemplate;